import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Container, Grid, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import loyaltyService from '../../services/loyaltyService';
import useBrandPinIcon from '../../hooks/useBrandPinIcon';
import StoreCard from './StoreCard';
import StorePopup from './StorePopup';
import { calculateMapBounds, matchesSearch, storeCardRows, withDistances } from './storeLocations';

// The list and the map share the screen's height on a desktop, each scrolling or panning on its own; the offset leaves
// room for the app bar, the page heading and the search box.
const DESKTOP_PANE_HEIGHT = 'calc(100vh - 260px)';
const DESKTOP_PANE_MIN_HEIGHT = 420;
const PHONE_MAP_HEIGHT = 260;
// How close the map zooms when a member picks a store from the list.
const SELECTED_STORE_ZOOM = 14;

/**
 * Flies the map to a store picked from the list and opens its pin's popup. A store picked on the map needs neither:
 * its pin is already in view and Leaflet opens the popup itself.
 */
const FlyToStore = ({ selection, locations, markerRefs }) => {
  const map = useMap();
  useEffect(() => {
    if (!selection?.fromList) return;
    const location = locations.find((candidate) => candidate.id === selection.id);
    if (!location?.lat || !location?.lon) return;
    map.flyTo([location.lat, location.lon], Math.max(map.getZoom(), SELECTED_STORE_ZOOM));
    markerRefs.current[location.id]?.openPopup();
    // Only a new pick moves the map; `locations` changing (distances arriving) must not fly it back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, map, markerRefs]);
  return null;
};

/**
 * Every store in a loyalty group: a searchable list beside a map with a pin for each one, reached from "See all N
 * locations" on the membership page (CIN-7192).
 *
 * WHY a page of its own: the membership page shows the nearest few as cards, which stops working for a program with
 * hundreds of stores. This is the usual store-locator layout, list left and map right; on a phone the map sits above
 * the list. Picking a store in the list flies the map to it, and picking a pin selects its card.
 *
 * Stores are nearest first once the member allows location access, the same as on the membership page; the map shows
 * every store whatever the search, and the search narrows only the list.
 */
const GroupLocationsPage = () => {
  const { companyId: groupId } = useParams();
  const navigate = useNavigate();
  const pinIcon = useBrandPinIcon();
  const [group, setGroup] = useState(null);
  const [error, setError] = useState(null);
  const [position, setPosition] = useState(null);
  const [search, setSearch] = useState('');
  // The store picked in the list or on the map; `fromList` says whether the map should fly to it.
  const [selection, setSelection] = useState(null);
  const markerRefs = useRef({});
  const cardRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    // Clear the last group so its stores never show under another's name while this one loads.
    setGroup(null);
    setError(null);
    loyaltyService
      .getGroupDetails(groupId)
      .then((response) => {
        if (!cancelled) setGroup(response.group);
      })
      .catch((err) => {
        console.error('Failed to load store locations:', err);
        if (!cancelled) setError('These locations could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  useEffect(() => {
    if (!navigator.geolocation) return undefined;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (result) => {
        if (!cancelled) setPosition({ lat: result.coords.latitude, lon: result.coords.longitude });
      },
      // Declining is fine: the stores keep the server's order and show no distances.
      (geoError) => console.log('Could not get user location:', geoError),
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const locations = useMemo(() => withDistances(group?.locations, position), [group, position]);
  const shown = useMemo(() => locations.filter((location) => matchesSearch(location, search)), [locations, search]);
  const rows = useMemo(() => storeCardRows(locations), [locations]);
  const bounds = useMemo(() => calculateMapBounds(locations), [locations]);

  const selectFromMap = (location) => {
    setSelection({ id: location.id, fromList: false });
    // The card may be hidden by the search; then there is nothing to scroll to.
    cardRefs.current[location.id]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const paneHeight = { md: DESKTOP_PANE_HEIGHT };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <IconButton onClick={() => navigate(`/memberships/${groupId}`)} sx={{ mb: 2 }} aria-label="Back to membership">
          <ArrowBackIcon />
        </IconButton>

        {!group && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}

        {group && (
          <>
            <Typography variant="h4" component="h1">
              Store locations
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {group.name} · {locations.length} {locations.length === 1 ? 'location' : 'locations'}
              {position ? ' · nearest first' : ''}
            </Typography>

            {locations.length === 0 ? (
              <Alert severity="info">This program has no store locations listed.</Alert>
            ) : (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 5 }} sx={{ order: { xs: 2, md: 1 } }}>
                  <TextField
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search by name, city or ZIP"
                    size="small"
                    fullWidth
                    sx={{ mb: 2 }}
                    slotProps={{
                      htmlInput: { 'aria-label': 'Search store locations' },
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <Box sx={{ height: paneHeight, minHeight: { md: DESKTOP_PANE_MIN_HEIGHT }, overflowY: { md: 'auto' }, pr: { md: 1 } }}>
                    {shown.length === 0 ? (
                      <Typography color="text.secondary">No locations match "{search.trim()}".</Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {shown.map((location) => (
                          <Box
                            key={location.id}
                            ref={(element) => {
                              cardRefs.current[location.id] = element;
                            }}
                          >
                            <StoreCard
                              location={location}
                              rows={rows}
                              selected={location.id === selection?.id}
                              onSelect={() => setSelection({ id: location.id, fromList: true })}
                            />
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 7 }} sx={{ order: { xs: 1, md: 2 } }}>
                  {/* Without coordinates there is nothing to map ("Invalid LatLng (0, NaN)"); the list still shows. */}
                  {bounds && (
                    <Box sx={{ height: { xs: PHONE_MAP_HEIGHT, ...paneHeight }, minHeight: { md: DESKTOP_PANE_MIN_HEIGHT } }}>
                      <MapContainer
                        bounds={bounds}
                        // One location gives zero-area bounds; capping the fit keeps it from zooming in without limit.
                        boundsOptions={{ maxZoom: 15 }}
                        style={{ height: '100%', width: '100%', borderRadius: '4px' }}
                        // On a phone a one-finger drag pans the map instead of scrolling the page, which would trap a
                        // member above the list. Pinch and the +/- buttons still zoom.
                        dragging={!L.Browser.mobile}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {locations
                          .filter((location) => location.lat && location.lon)
                          .map((location) => (
                            <Marker
                              key={location.id}
                              position={[location.lat, location.lon]}
                              icon={pinIcon}
                              // Names the pin for screen readers: a div icon has no alt text.
                              title={location.friendly_name || location.name}
                              ref={(marker) => {
                                markerRefs.current[location.id] = marker;
                              }}
                              eventHandlers={{ click: () => selectFromMap(location) }}
                            >
                              <Popup>
                                <StorePopup location={location} />
                              </Popup>
                            </Marker>
                          ))}
                        <FlyToStore selection={selection} locations={locations} markerRefs={markerRefs} />
                      </MapContainer>
                    </Box>
                  )}
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default GroupLocationsPage;
